// LeadCommunicationCenter — Phase 4.
//
// Compact unified view of all interactions with this lead across
// every channel — calls, texts, emails, activity events. Feeds off
// the same callHistory + outreachLog + lead.activity props the
// existing LeadTimeline consumes, so the two always reconcile.
//
// Two things stack:
//   1. Channel breakdown ribbon — call/text/email/other counts
//      with the "last-touched" heat pill for each channel
//   2. Freshness card — last-touch date/channel/agent + a
//      "channels legal right now" hint from the lead's opt-out
//      + consent state
//
// This is intentionally a SUMMARY. The full timeline lives right
// below it in LeadTimeline — no duplication.

import React from 'react';

function ageDays(iso) {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  return Math.floor((Date.now() - t) / 86_400_000);
}

function ageLabel(days) {
  if (days == null) return '—';
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7)  return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

function tempFromDays(days) {
  if (days == null) return { color: '#94a3b8', label: 'never' };
  if (days <= 2)  return { color: '#059669', label: 'hot' };
  if (days <= 7)  return { color: '#d97706', label: 'warm' };
  if (days <= 30) return { color: '#64748b', label: 'cool' };
  return { color: '#94a3b8', label: 'cold' };
}

function Row({ icon, channel, count, lastAt }) {
  const days = ageDays(lastAt);
  const temp = tempFromDays(days);
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      background: '#ffffff', border: '1px solid #e2e8f0',
      borderRadius: 8, padding: '8px 12px',
    }}>
      <div style={{ fontSize: 18 }}>{icon}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12, color: '#0f172a', fontWeight: 700 }}>{channel}</div>
        <div style={{ fontSize: 10, color: '#64748b', marginTop: 1 }}>{count} total</div>
      </div>
      <div style={{
        color: temp.color,
        fontSize: 11, fontWeight: 700,
        display: 'flex', alignItems: 'center', gap: 4,
      }}>
        <span style={{
          display: 'inline-block', width: 6, height: 6, borderRadius: 999, background: temp.color,
        }}/>
        {ageLabel(days)}
      </div>
    </div>
  );
}

export default function LeadCommunicationCenter({ lead, callHistory = [], outreachLog = [] }) {
  // Bucket outreach entries by type; the drawer's outreach log uses
  // `type` in {'call','sms','email','other'}.
  const smsRows   = outreachLog.filter(o => o.type === 'sms');
  const emailRows = outreachLog.filter(o => o.type === 'email');
  const otherRows = outreachLog.filter(o => !['sms', 'email', 'call'].includes(o.type));

  const lastCall  = callHistory[0]?.at || callHistory[0]?.created_at || null;
  const lastSms   = smsRows[0]?.at   || smsRows[0]?.created_at   || null;
  const lastEmail = emailRows[0]?.at || emailRows[0]?.created_at || null;

  // Compliance / channel legality hints from what the lead already
  // carries (no new tables). optOutSms / optOutEmail flags come from
  // the existing consent flow in the drawer.
  const canSms   = !lead?.optOutSms   && Boolean(lead?.phone);
  const canEmail = !lead?.optOutEmail && Boolean(lead?.email);
  const canCall  = !lead?.doNotCall   && Boolean(lead?.phone);

  const totalTouches = callHistory.length + outreachLog.length;

  return (
    <div style={{
      background: '#eff6ff',
      border: '1px solid #bfdbfe',
      borderRadius: 10, padding: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
        <div style={{ fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#1d4ed8', fontWeight: 800 }}>
          Communication Center
        </div>
        <div style={{ fontSize: 11, color: '#3730a3' }}>
          {totalTouches} total touch{totalTouches === 1 ? '' : 'es'}
        </div>
      </div>

      <div style={{
        display: 'grid', gap: 6,
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
      }}>
        <Row icon="📞"  channel="Calls"  count={callHistory.length} lastAt={lastCall}/>
        <Row icon="💬"  channel="SMS"    count={smsRows.length}     lastAt={lastSms}/>
        <Row icon="✉️ " channel="Email"  count={emailRows.length}   lastAt={lastEmail}/>
        {otherRows.length > 0 && (
          <Row icon="📋" channel="Other" count={otherRows.length}
               lastAt={otherRows[0]?.at || otherRows[0]?.created_at}/>
        )}
      </div>

      {/* Channels legal right now */}
      <div style={{
        marginTop: 10, padding: '8px 10px', background: '#ffffff',
        border: '1px solid #bfdbfe', borderRadius: 6,
        fontSize: 11, color: '#1e40af',
      }}>
        <div style={{
          fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase',
          color: '#1e3a8a', fontWeight: 800, marginBottom: 4,
        }}>Legal to send right now</div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ color: canCall ? '#059669' : '#94a3b8', fontWeight: 700 }}>{canCall ? '✓' : '✕'} Call</span>
          <span style={{ color: canSms   ? '#059669' : '#94a3b8', fontWeight: 700 }}>{canSms   ? '✓' : '✕'} SMS</span>
          <span style={{ color: canEmail ? '#059669' : '#94a3b8', fontWeight: 700 }}>{canEmail ? '✓' : '✕'} Email</span>
        </div>
      </div>
    </div>
  );
}
